import {io, Socket} from 'socket.io-client';
import store from '@/store';
import {NotificationTypes} from '@/enums/notifier/NotificationTypes';
import {LeanDocument, Types} from 'mongoose';
import {NotifierEvents} from '@/enums/notifier/NotifierEvents';
import {NotificationBus} from '@/busses/NotificationBus';
import {SoundManager} from '@/classes/clientOnly/SoundManager.class';
import {RecorderEvents} from '@/enums/notifier/serviceEvents/RecorderEvents';
import {RecorderEventBus} from '@/busses/RecorderEventBus';
import {extractEnumValues} from '@/helpers/extractEnumValues';
import {MessengerEvents} from '@/enums/notifier/serviceEvents/MessengerEvents';
import {MessengerEventBus} from '@/busses/MessengerEventBus';
import {PlacetelEvents} from '@/enums/notifier/serviceEvents/PlacetelEvents';
import {PlacetelCallBus} from '@/busses/PlacetelCallBus';
import {IncomingNotification, NotificationLike} from '@/types/NotificationLike.type';
import {NotificationDocument} from '@/documentTypes/notifier/Notification.document';
import {
    channelEnabled,
    loadUserPreferences,
    showBrowserNotification,
} from '@/helpers/notificationPreferences.helper';
import {toNotificationView} from '@/components/app/appBar/notifications/notificationCenter/notificationView.helper';
import {
    claimAlert,
    initNotificationTabSync,
    rememberAlerted,
    wasAlertedInAnyTab,
    wasSeenInAnyTab,
} from '@/helpers/notificationTabSync.helper';

/** Sound per type (as before); dueToDo like overdueToDo */
const SOUNDS: Record<string, () => Promise<void>> = {
    [NotificationTypes.MENTION]: () => SoundManager.sounds.silent(),
    [NotificationTypes.CALL]: () => SoundManager.sounds.call(),
    [NotificationTypes.OVERDUE_TO_DO]: () => SoundManager.sounds.reminder(),
    [NotificationTypes.OVERDUE_TO_DO_DIGEST]: () => SoundManager.sounds.reminder(),
    [NotificationTypes.DUE_TO_DO]: () => SoundManager.sounds.reminder(),
    [NotificationTypes.TO_DO_ASSIGNED]: () => SoundManager.sounds.common(),
    [NotificationTypes.HANDOUT_EVENT_FEEDBACK]: () => SoundManager.sounds.common(),
    [NotificationTypes.TO_DO_COMPLETED]: () => SoundManager.sounds.completed(),
    [NotificationTypes.ENTITY_ASSIGNED_TO_SUPERVISOR]: () => SoundManager.sounds.silent(),
    [NotificationTypes.SUPERVISED_ENTITY_DEACTIVATED]: () => SoundManager.sounds.silent(),
    [NotificationTypes.WHATSAPP_MESSAGE_TEMPLATE_STATUS_UPDATE]: () => SoundManager.sounds.silent(),
    [NotificationTypes.COURSE_CANCELLATION_FAILED]: () => SoundManager.sounds.silent(),
    [NotificationTypes.MISSING_COURSE_LOGS]: () => SoundManager.sounds.silent(),
    [NotificationTypes.TAKEOVER_OFFER_UPDATE]: () => SoundManager.sounds.common(),
    [NotificationTypes.QUALITY_CASE_DIGEST]: () => SoundManager.sounds.common(),
    [NotificationTypes.TEACHER_SICK_REPORT]: () => SoundManager.sounds.common(),
    [NotificationTypes.APPLICATION_WITHDRAWN]: () => SoundManager.sounds.common(),
    [NotificationTypes.APPLICANT_DELETION_REQUESTED]: () => SoundManager.sounds.common(),
    [NotificationTypes.DHL_PICKUP_UPDATE]: () => SoundManager.sounds.common(),
};

/** Types delivered as pop-up; `common` has no producer and stays panel-only as before */
const POPUP_TYPES = Object.keys(SOUNDS);

const playSound = (type: string) => {
    SOUNDS[type]?.().catch(() => undefined);
};

/**
 * A live event that another tab already handled within this time is not shown again (socket events reach the tabs a
 * few ms apart). Longer is not possible: a snoozed notification comes again later with the same id.
 */
const LIVE_SEEN_WINDOW_MS = 60 * 1000;

/**
 * This class is used for communicating with the notifier microservice.
 * Notify v2: settings per type (Ton / Pop-up / Browser / aktiv), browser notifications in background tabs and
 * catch-up of missed notifications after a reconnect.
 * @class NotifierSocketApi
 * @author Joshua Seipel
 */
export class NotifierSocketApi {
    static service = 'notifier';
    private static socket: Socket;
    /** Time of the last disconnect, for the catch-up after the reconnect */
    private static disconnectedAt: Date | undefined;
    /** Ids received via socket in this page view (no duplicate pop-ups after a catch-up) */
    private static received = new Set<string>();

    static async connect() {
        this.socket = io(`${process.env.VUE_APP_API_PATH}/${this.service}`, {
            path: '/notifier/socket.io',
            withCredentials: true,
        });
        this.received.clear();
        this.disconnectedAt = undefined;
        initNotificationTabSync(store);
        loadUserPreferences(store.state.loginUserId).catch(() => undefined);
        this.socket.on('connect', () => {
            store.commit('setSocketConnected', true);
            if (this.disconnectedAt) {
                const since = this.disconnectedAt;
                this.disconnectedAt = undefined;
                this.catchUp(since);
            }
        });
        this.socket.on('disconnect', () => {
            store.commit('setSocketConnected', false);
            this.disconnectedAt = new Date();
        });
        for (const type of POPUP_TYPES) {
            this.socket.on(type, (data: IncomingNotification) => this.receive(type, data, true));
        }

        // Read/deleted in any tab or device: the server tells every tab, the pop-ups disappear everywhere
        this.socket.on(NotifierEvents.MARK_READ, (ids: Types.ObjectId[]) => {
            store.commit('hidePopups', ids);
            NotificationBus.$emit(NotifierEvents.MARK_READ, ids);
        });
        this.socket.on(NotifierEvents.DISMISS, (ids: Types.ObjectId[]) => {
            store.commit('hidePopups', ids);
            NotificationBus.$emit(NotifierEvents.DISMISS, ids);
        });
        this.socket.on(NotifierEvents.REVOKE_DISMISSION, (ids: Types.ObjectId[]) => {
            NotificationBus.$emit(NotifierEvents.REVOKE_DISMISSION, ids);
        });

        // Handle Recorder events
        for (const v of extractEnumValues(RecorderEvents) as any[]) {
            this.socket.on(v, (data: any) => RecorderEventBus.$emit(v, data));
        }

        // Handle Messenger events
        for (const v of extractEnumValues(MessengerEvents) as any[]) {
            this.socket.on(v, (data: any) => MessengerEventBus.$emit(v, data));
        }

        // Placetel calls of this person (Anruf-Fenster)
        for (const v of extractEnumValues(PlacetelEvents) as PlacetelEvents[]) {
            this.socket.on(v, (data: any) => PlacetelCallBus.$emit(v, data));
        }
    }

    /**
     * One incoming notification: panel always; pop-up, sound and browser notification by the user's settings.
     * Live events are always shown (a snoozed notification comes again with the same id), catch-up skips known ids.
     * With several tabs: no pop-up when another tab already hid it, sound and browser notification in one tab only.
     */
    private static receive(type: string, data: IncomingNotification, live: boolean) {
        const id = String(data._id);
        if (!live && this.received.has(id)) {
            return;
        }
        this.received.add(id);
        const seenElsewhere = wasSeenInAnyTab(id, live ? LIVE_SEEN_WINDOW_MS : undefined);
        const enabled = channelEnabled(type, 'enabled');
        const popup = enabled && channelEnabled(type, 'popup') && !seenElsewhere;
        store.commit('addRtcNotification', {...data, showInApp: popup});
        if (!enabled || seenElsewhere) {
            return;
        }
        const kind = live ? 'live' : 'catch-up';
        if (live && channelEnabled(type, 'sound')) {
            claimAlert(`${id}:${kind}:sound`).then((mine) => {
                if (mine) {
                    rememberAlerted(id);
                    playSound(type);
                }
            }).catch(() => undefined);
        }
        // only hidden tabs show browser notifications (as before); of several hidden tabs only one
        if (channelEnabled(type, 'browser') && typeof document !== 'undefined' && document.hidden) {
            claimAlert(`${id}:${kind}:browser`).then((mine) => mine && this.showInBrowser(data, id)).catch(() => undefined);
        }
    }

    private static showInBrowser(data: IncomingNotification, id: string) {
        const notification = {...data, showInApp: false} as NotificationLike;
        const view = toNotificationView(notification);
        showBrowserNotification(view.title, view.text || view.lines[0], (data as { groupKey?: string }).groupKey || id, () => {
            NotificationBus.$emit('openNotification', notification);
        });
    }

    /**
     * After a reconnect: loads what was created meanwhile (new notifier), otherwise lets the panel reload.
     * Missed notifications appear in the panel and as pop-ups, with at most one sound – and none for notifications
     * another tab already rang for (a background tab often reconnects after the others received the event live).
     */
    private static async catchUp(since: Date) {
        // a minute of overlap: clocks of client and server differ slightly; duplicates are skipped by id
        // lazy: the router imports this file, a static import of an API class would be an import cycle
        const {NotifierApi} = await import('@/classes/api/notifier.api');
        const missed = await NotifierApi.getOwnCreatedAfter(new Date(since.getTime() - 60000));
        if (!missed) {
            NotificationBus.$emit('update');
            return;
        }
        const fresh = missed
            .filter((n: LeanDocument<NotificationDocument>) => !n.readAt && !n.dismissedAt && !this.received.has(String(n._id)))
            .reverse();
        fresh.forEach((n) => this.receive(n.type, n as unknown as IncomingNotification, false));
        const audible = fresh.find((n) => channelEnabled(n.type, 'enabled') && channelEnabled(n.type, 'sound')
            && !wasSeenInAnyTab(String(n._id)) && !wasAlertedInAnyTab(String(n._id)));
        if (audible && await claimAlert(`${String(audible._id)}:catch-up-sound`)) {
            rememberAlerted(String(audible._id));
            playSound(audible.type);
        }
    }

    static async disconnect(): Promise<void> {
        return new Promise((res) => {
            this.disconnectedAt = undefined;
            this.socket.disconnect();
            res();
        });
    }
}
